import React from 'react';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { DumboProps as Props } from '../types';
import { getInfoToDisplayImageDeviation } from './_helpers';
import Unknown from '../Unknown';
import Flag from '@wix/da-react-context/pkg/flags/Flag';
import PlayVideoIcon from '@wix/da-ds/pkg/Icons/72x72/PlayVideo';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useLazyLoading } from '../../../LazyLoadingImage/useLazyLoading';
import { isValidFileSrc, getDeviationType } from '../../_helpers';
import { DeviationTypes } from '../../../types/deviation';
import SeoData from '../_partials/SeoData';
import s from './Image.scss';

export interface ExtraProps {
  extraWrapStyles?: React.CSSProperties;
  videoIconSize?: IconSize;
}

/**
 * If you are changing any fundamentals here please remember to also
 * consider the DaEditorWrappers image views, they use this to generate
 * HTML and react version of editor text.
 */
const ImageDumbView: React.FC<Props & ExtraProps> = (
  props: Props & ExtraProps
) => {
  const {
    deviation,
    className,
    width: propsWidth,
    height: propsHeight,
    allowPreviewUsage = true,
    useBackgroundColor = true,
    greenLikeCrop,
    noCropping,
    withSeoData,
    extraWrapStyles,
    videoIconSize,
    'aria-hidden': ariaHidden,
  } = props;
  const { ref, inView, enableLazyLoading, supportsNativeLazy } =
    useLazyLoading(true);
  const deviationType = getDeviationType(deviation);
  const isVideo = deviationType === DeviationTypes.FILM;

  if (!propsWidth && !propsHeight) {
    // no use-cases for responsive image views
    ddt.warn(
      'deviationviews',
      'Deviation image is not rendered because of missing Width and Height properties'
    );
    return null;
  }

  const info = getInfoToDisplayImageDeviation({
    deviation,
    height: propsHeight,
    width: propsWidth,
    allowPreviewUsage,
    greenLikeCrop,
    noCropping,
  });

  if (!info || !info.pickedImage || !isValidFileSrc(info.pickedImage.src)) {
    ddt.warn(
      'deviationviews',
      'Could not find a suitable image file to render deviation image',
      info
    );
    return <Unknown {...props} />;
  }

  const { height, width, imgStyles, pickedImage } = info;
  ddt.log('deviationviews.thumbtype', `Used ${pickedImage.type}`);

  const imageProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    // 1px transparent png
  };
  const wrapStyles: React.CSSProperties = {
    ...extraWrapStyles,
    width,
    height,
  };
  const imageStyles: React.CSSProperties = {
    width,
    height,
    ...imgStyles,
  };

  // Native lazy loading is simple
  if (enableLazyLoading && supportsNativeLazy) {
    (imageProps as any).loading = 'lazy';
    imageProps.src = pickedImage.src;
  }

  // Intersection Observer lazy loading
  if (enableLazyLoading && !supportsNativeLazy) {
    if (inView) {
      imageProps.src = pickedImage.src;
    }
  }

  // Standard non lazy loaded
  if (!enableLazyLoading) {
    imageProps.src = pickedImage.src;
  }

  if (pickedImage.srcSet) {
    imageProps.srcSet = pickedImage.srcSet;
  }

  const seoProps = withSeoData
    ? {
      'typeof': 'ImageObject',
      vocab: 'https://schema.org/',
    } : {};
  const imgSeoProps = withSeoData
    ? { 'property': 'contentUrl' }
    : {};

  return (
    <div
      ref={enableLazyLoading && !supportsNativeLazy ? ref : undefined}
      className={classnames(
        s['wrapper'],
        className,
        !useBackgroundColor && s['no-background']
      )}
      style={wrapStyles}
      aria-hidden={ariaHidden}
      data-testid="thumb"
      {...seoProps}
    >
      {renderDebug(
        info.pickedImage,
        propsWidth,
        propsHeight,
        enableLazyLoading
      )}
      <img
        alt={deviation.title}
        style={imageStyles}
        {...imageProps}
        {...imgSeoProps}
      />
      {withSeoData && <SeoData deviation={deviation} />}
      {isVideo && videoIconSize && (
        <PlayVideoIcon
          size={videoIconSize}
          className={s['video-icon']}
          disableDefaultColors
        />
      )}
      {enableLazyLoading && !supportsNativeLazy && (
        <noscript>
          <img
            alt={deviation.title}
            src={pickedImage.src}
            srcSet={pickedImage.srcSet}
            style={imageStyles}
          />
        </noscript>
      )}
    </div>
  );
};
ImageDumbView.displayName = 'ImageDumbView';

function renderDebug(pickedImage, propsWidth, propsHeight, isLazy) {
  return (
    <Flag condition="thumbdebug">
      <div className={s['thumb-debug']}>
        {isLazy && 'lazy/'}
        {pickedImage.type}/
        {pickedImage.src.replace(/\?.*/, '').split('.').pop()}/
        {pickedImage.transparency === true ? 'true' : 'false'}/
        <abbr title="imageDims">
          {pickedImage.width}x{pickedImage.height}
        </abbr>
        /
        <abbr title="blockDims">
          {propsWidth}x{propsHeight}
        </abbr>
      </div>
    </Flag>
  );
}

export default ImageDumbView;
